import { Button } from "@/components/elements/Button";
import { Box, Stack, Typography } from "@mui/material";
import React, { FC, useMemo, useRef, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FilterConditionToolbar, TSavedCondition as TToolbarSavedCondition } from "@irohana-dev/components";
import { black } from "@/theme/colors";
import { IconFormatListBulleted } from "@/assets/icons";
import { TPagination } from "@/types/api";
import { CaseListFilterConditionPanel } from "@/features/case/components/CaseList/CaseListFilterConditionPanel";
import { useVisible } from "@/hooks/useVisible";
import { CaseListSavedConditionsListModal, TCaseListSavedCondition } from "@/features/case/components/CaseList/CaseListSavedConditionsListModal";
import { CaseListAppliedConditionDetailPopover } from "@/features/case/components/CaseList/CaseListAppliedConditionDetailPopover";
import {
  TCaseListDisplayColumnSetting,
  TCaseListFilterFormExtended,
  defaultCaseListDisplayColumns,
  defaultCaseListFilterValues,
  isEmptyFilterValue,
  normalizeCaseDateFields,
  normalizeMultiSelectFields,
} from "@/features/case/components/CaseList/case-list-filter-shared";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { applyColumnSettings, hasDisplayColumnsChanged } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import { useDeleteSavedCondition, useSavedConditionList } from "@/api/services/main/saved-condition";
import { CASE_LIST_DISPLAY_COLUMNS_STORAGE_KEY } from "@/features/case/constants/storage-keys";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";


type Props = {
  pagination?: TPagination;
  onApplyAdvancedFilters?: (filters: TCaseListFilterFormExtended) => void;
  onApplyDisplaySettings?: (columns: TCaseListDisplayColumnSetting[]) => void;
  appliedConditionName?: string;
  appliedConditionId?: number | null;
  onAppliedConditionNameChange?: (name: string) => void;
  onAppliedConditionIdChange?: (id: number | null) => void;
  currentDisplayColumns?: TCaseListDisplayColumnSetting[] | null;
  currentAdvancedFilters?: TCaseListFilterFormExtended | null;
};

export const CaseListTop: FC<Props> = ({
  pagination,
  onApplyAdvancedFilters,
  onApplyDisplaySettings,
  appliedConditionName,
  appliedConditionId,
  onAppliedConditionNameChange,
  onAppliedConditionIdChange,
  currentDisplayColumns,
  currentAdvancedFilters,
}) => {
  const savedConditionsModal = useVisible();
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [filterMode, setFilterMode] = useState<"create" | "edit">("create");
  const [conditionDetailAnchorEl, setConditionDetailAnchorEl] = useState<HTMLElement | null>(null);
  const { data: savedConditionsData } = useSavedConditionList("case");

  const handleOpenCreateFilter = () => {
    setFilterMode("create");
    setFilterAnchorEl(document.body);
  };

  const handleOpenEditFilter = () => {
    setFilterMode("edit");
    setFilterAnchorEl(document.body);
  };

  const handleFilterPopoverClose = () => {
    setFilterAnchorEl(null);
  };

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  const justSavedConditionRef = useRef(false);

  const handleApplyFiltersFromPanel = (filters: TCaseListFilterFormExtended) => {
    onApplyAdvancedFilters?.(filters);
    if (justSavedConditionRef.current) {
      justSavedConditionRef.current = false;
      return;
    }
    if (filterMode === "create") {
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    }
  };

  const handleConditionSaved = (id: number, title: string) => {
    justSavedConditionRef.current = true;
    onAppliedConditionNameChange?.(title);
    onAppliedConditionIdChange?.(id);
    setFilterMode("edit");
  };

  const appliedCondition =
    filterMode === "edit" && appliedConditionId
      ? savedConditionsData?.data.find((item) => Number(item.id) === Number(appliedConditionId))
      : undefined;

  const savedConditions = useMemo<TToolbarSavedCondition[]>(
    () =>
      (savedConditionsData?.data ?? []).map((item) => ({
        id: Number(item.id),
        name: item.title,
        shareScope: item.shareScope ?? "self",
        isDefault: item.isDefault ?? false,
        badgeColor: item.defaultColor ?? undefined,
      })),
    [savedConditionsData],
  );

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const deleteSavedCondition = useDeleteSavedCondition();

  const applySavedColumnSettings = (picked: { includeColumnSettings?: boolean; columnSettings?: unknown }) => {
    if (!picked.includeColumnSettings || !picked.columnSettings) return;
    const settings = picked.columnSettings as { order?: string[]; hidden?: string[] };
    const base = currentDisplayColumns ?? [...defaultCaseListDisplayColumns];
    const next = applyColumnSettings(base, settings.order ?? [], settings.hidden ?? [], defaultCaseListDisplayColumns);
    onApplyDisplaySettings?.(next);
    localStorage.setItem(CASE_LIST_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(next));
  };

  const handleSelectCondition = (id: string | number) => {
    const picked = savedConditionsData?.data.find((item) => Number(item.id) === Number(id));
    if (!picked) return;
    onApplyAdvancedFilters?.({
      ...defaultCaseListFilterValues,
      ...normalizeMultiSelectFields(normalizeCaseDateFields(picked.filters ?? {}) as Partial<Record<string, unknown>>),
    } as TCaseListFilterFormExtended);
    applySavedColumnSettings(picked);
    onAppliedConditionNameChange?.(picked.title);
    onAppliedConditionIdChange?.(Number(picked.id));
  };

  const handleDeleteAppliedCondition = async () => {
    if (!appliedConditionId) return;
    try {
      await deleteSavedCondition.mutateAsync(appliedConditionId);
      toast.success("条件を削除しました。");
      onApplyAdvancedFilters?.({ ...defaultCaseListFilterValues });
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    } catch (e) {
      showError(e);
    } finally {
      setIsDeleteConfirmOpen(false);
    }
  };

  const handleApplySavedCondition = (condition: TCaseListSavedCondition) => {
    onApplyAdvancedFilters?.(condition.filters);
    onAppliedConditionNameChange?.(condition.title);
    onAppliedConditionIdChange?.(condition.id);
  };

  const activeFilterCount = useMemo(() => {
    if (!currentAdvancedFilters) return 0;
    const { registrationDateFrom, registrationDateTo, ...rest } = currentAdvancedFilters;
    let count = Object.values(rest).filter((v) => !isEmptyFilterValue(v)).length;
    if (registrationDateFrom || registrationDateTo) count += 1;
    return count;
  }, [currentAdvancedFilters]);

  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  const handleClearAppliedFilter = () => {
    onApplyAdvancedFilters?.(defaultCaseListFilterValues);
    onAppliedConditionNameChange?.("");
    onAppliedConditionIdChange?.(null);
    onApplyDisplaySettings?.([...defaultCaseListDisplayColumns]);
    localStorage.setItem(CASE_LIST_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(defaultCaseListDisplayColumns));
    setIsClearConfirmOpen(false);
  };

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="body14Semi">
            全 {formatTotal(pagination?.total) ?? 0} 件中 {Math.min((pagination?.page ?? 1) * (pagination?.perPage ?? 20), pagination?.total ?? 0)}{" "}
            件を表示
          </Typography>

          <FilterConditionToolbar
            onOpenFilter={handleOpenCreateFilter}
            activeCount={
              activeFilterCount || Number(Boolean(appliedConditionId) || hasDisplayColumnsChanged(currentDisplayColumns, defaultCaseListDisplayColumns))
            }
            conditions={savedConditions}
            selectedId={appliedConditionId ?? undefined}
            onSelect={handleSelectCondition}
            onClearSelection={() => setIsClearConfirmOpen(true)}
            onEdit={handleOpenEditFilter}
            onDelete={() => setIsDeleteConfirmOpen(true)}
            shareColor={filterConditionShareColor}
          />

          {appliedConditionName && (
            <Box
              component="span"
              onClick={(e: React.MouseEvent<HTMLElement>) => setConditionDetailAnchorEl(e.currentTarget)}
              sx={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
            >
              <InfoOutlinedIcon sx={{ color: black[80], fontSize: 16 }} />
            </Box>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Button variant="outline" size="sm" startIcon={<IconFormatListBulleted width={20} height={20} />} onClick={savedConditionsModal.open}>
            保存した条件一覧
          </Button>
        </Stack>
      </Box>

      <CaseListFilterConditionPanel
        mode={filterMode}
        isOpen={Boolean(filterAnchorEl)}
        onClose={handleFilterPopoverClose}
        onApplyFilters={handleApplyFiltersFromPanel}
        onApplyDisplaySettings={onApplyDisplaySettings}
        initialDisplayColumns={currentDisplayColumns || undefined}
        initialFilters={filterMode === "edit" ? currentAdvancedFilters || undefined : defaultCaseListFilterValues}
        appliedConditionId={filterMode === "edit" ? appliedConditionId : null}
        appliedConditionName={filterMode === "edit" ? appliedConditionName : ""}
        appliedConditionShareScope={appliedCondition?.shareScope}
        appliedConditionIsDefault={appliedCondition?.isDefault}
        appliedConditionIncludeColumnSettings={appliedCondition?.includeColumnSettings}
        appliedConditionColumnSettings={appliedCondition?.columnSettings as { order?: string[]; hidden?: string[] } | null | undefined}
        onConditionSaved={handleConditionSaved}
      />
      <CaseListSavedConditionsListModal
        isOpen={savedConditionsModal.visible}
        onClose={savedConditionsModal.close}
        onApply={handleApplySavedCondition}
        pageType="case"
        appliedConditionId={appliedConditionId}
        onConditionTitleChange={(_id, newTitle) => onAppliedConditionNameChange?.(newTitle)}
      />
      <CaseListAppliedConditionDetailPopover
        anchorEl={conditionDetailAnchorEl}
        onClose={() => setConditionDetailAnchorEl(null)}
        filterValues={currentAdvancedFilters ?? null}
      />
      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onOk={handleClearAppliedFilter}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
      <ConfirmDeleteModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onOk={handleDeleteAppliedCondition}
        message="この条件を削除してもよろしいですか？"
        title=""
      />
    </>
  );
};
